import React, { useState, useRef, useEffect } from 'react';
import { FiBell, FiCheck, FiCheckCircle, FiClock, FiUser, FiAlertCircle, FiX } from 'react-icons/fi';
import { useNotifications } from '../../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import s from './FloatingButtons.module.css';

const ICON_MAP = { system: FiBell, account: FiUser, reminder: FiClock };
const PRIORITY_COLORS = { low: 'var(--text-muted)', normal: 'var(--text-secondary)', high: '#d4a052', urgent: '#e06c75' };

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function FloatingNotificationButton() {
  const { notifications, unreadCount, markRead, markAllRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClick = (n) => {
    if (n.statusCode !== 'read') markRead(n.id);
    if (n.actionUrl && navigate) { navigate(n.actionUrl); setOpen(false); }
  };

  const unread = notifications.filter(n => n.statusCode !== 'read');
  const read = notifications.filter(n => n.statusCode === 'read');

  return (
    <div className={`${s.fab} ${s.fabLeft}`} ref={ref}>
      <button className={s.button} onClick={() => setOpen(!open)} title="Notifications">
        <FiBell />
        {unreadCount > 0 && <span className={s.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && (
        <div className={`${s.menu} ${s.menuLeft} ${s.menuWide}`}>
          <div className={s.menuHeader}>
            <span className={s.menuHeaderTitle}>Notifications</span>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {unreadCount > 0 && (
                <button className={s.menuHeaderAction} onClick={markAllRead} title="Mark all as read">
                  <FiCheckCircle style={{ marginRight: 3, verticalAlign: 'middle' }} /> Read all
                </button>
              )}
              <button className={s.menuHeaderAction} onClick={() => setOpen(false)} style={{ padding: '2px 4px' }}>
                <FiX />
              </button>
            </div>
          </div>

          <div className={s.notifList}>
            {notifications.length === 0 ? (
              <div className={s.emptyNotif}>
                <FiBell style={{ fontSize: '2rem', opacity: 0.3, display: 'block', margin: '0 auto 8px' }} />
                No notifications yet
              </div>
            ) : (
              <>
                {unread.length > 0 && (
                  <>
                    <div style={{ padding: '6px 16px', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--accent)', background: 'var(--bg-primary)' }}>
                      New ({unread.length})
                    </div>
                    {unread.map(n => <NotifItem key={n.id} n={n} onClick={() => handleClick(n)} onRead={() => markRead(n.id)} />)}
                  </>
                )}
                {read.length > 0 && (
                  <>
                    <div style={{ padding: '6px 16px', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text-muted)', background: 'var(--bg-primary)' }}>
                      Earlier
                    </div>
                    {read.slice(0, 10).map(n => <NotifItem key={n.id} n={n} onClick={() => handleClick(n)} isRead />)}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotifItem({ n, onClick, onRead, isRead }) {
  const Icon = ICON_MAP[n.notificationTypeCode] || FiBell;
  const priorityColor = PRIORITY_COLORS[n.priority] || PRIORITY_COLORS.normal;

  return (
    <div className={s.notifItem} onClick={onClick} style={{ opacity: isRead ? 0.65 : 1, background: isRead ? 'transparent' : 'rgba(108,140,191,0.04)' }}>
      <div className={s.notifIcon} style={{ background: isRead ? 'var(--bg-primary)' : `${priorityColor}15` }}>
        <Icon style={{ color: priorityColor, fontSize: '0.9rem' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span className={s.notifText} style={{ fontWeight: isRead ? 400 : 600, color: isRead ? 'var(--text-muted)' : 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.title}</span>
          {n.priority === 'urgent' && <FiAlertCircle style={{ color: '#e06c75', fontSize: '0.7rem', flexShrink: 0 }} />}
          {n.priority === 'high' && <FiAlertCircle style={{ color: '#d4a052', fontSize: '0.7rem', flexShrink: 0 }} />}
        </div>
        {n.body && <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{n.body}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
          <span className={s.notifTime}>{timeAgo(n.createdAt)}</span>
          {!isRead && onRead && (
            <button onClick={e => { e.stopPropagation(); onRead(); }} style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '0.68rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
              <FiCheck size={10} /> Read
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
